import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";

const HEIGHT = 180;

/**
 * A canvas to draw a signature on with mouse, pen or finger.
 *
 * Exposes isEmpty(), clear() and toDataURL() (PNG) through its ref, and calls onChange(empty)
 * whenever that changes.
 */
const SignaturePad = forwardRef(({ onChange }, ref) => {
  const { t } = useTranslation("dashboard");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const points = useRef([]);
  const empty = useRef(true);

  const setEmpty = value => {
    if (empty.current !== value) {
      empty.current = value;
      onChange?.(value);
    }
  };

  // Size the backing store to the container and the device pixel ratio, so strokes stay sharp
  // on phones and retina screens. Resizing a canvas clears it, so when the container changes
  // width (rotation, modal opening on a phone) what was drawn is copied back unscaled.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    let currentWidth = 0;

    const fit = () => {
      const width = Math.floor(container.clientWidth);
      if (!width || width === currentWidth) return;
      const ratio = window.devicePixelRatio || 1;
      let snapshot = null;
      if (currentWidth && !empty.current) {
        snapshot = document.createElement("canvas");
        snapshot.width = canvas.width;
        snapshot.height = canvas.height;
        snapshot.getContext("2d").drawImage(canvas, 0, 0);
      }
      currentWidth = width;
      canvas.width = width * ratio;
      canvas.height = HEIGHT * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${HEIGHT}px`;
      const context = canvas.getContext("2d");
      if (snapshot) context.drawImage(snapshot, 0, 0);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.lineWidth = 2.2;
      context.lineCap = "round";
      context.lineJoin = "round";
      // Ink stays dark in every theme: the PNG is placed on white paper in the contract PDF.
      context.strokeStyle = "#1a2b6d";
    };

    fit();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const position = event => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = event => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawing.current = true;
    const point = position(event);
    points.current = [point];
    // A dot, so a tap leaves a mark too.
    const context = canvasRef.current.getContext("2d");
    context.beginPath();
    context.arc(point.x, point.y, 1.1, 0, Math.PI * 2);
    context.fillStyle = context.strokeStyle;
    context.fill();
    setEmpty(false);
  };

  const move = event => {
    if (!drawing.current) return;
    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const list = points.current;
    list.push(position(event));
    if (list.length < 3) return;
    // Quadratic curves through the midpoints give a smooth line instead of a polyline.
    const [a, b, c] = list.slice(-3);
    context.beginPath();
    context.moveTo((a.x + b.x) / 2, (a.y + b.y) / 2);
    context.quadraticCurveTo(b.x, b.y, (b.x + c.x) / 2, (b.y + c.y) / 2);
    context.stroke();
  };

  const end = () => {
    drawing.current = false;
    points.current = [];
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty.current,
    clear,
    toDataURL: () => canvasRef.current.toDataURL("image/png")
  }));

  return (
    <div className="signature-pad">
      <div className="signature-pad__area">
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          aria-label={t("Signature field")}
        />
        <span className="signature-pad__baseline" aria-hidden="true">
          ✕
        </span>
      </div>
      <Button type="link" className="signature-pad__clear" onClick={clear}>
        {t("Clear signature")}
      </Button>
    </div>
  );
});

export default SignaturePad;
